import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { hasSuperAdminRole } from '../utils/roles';
import { ADMIN_HOUSEHOLDS_QUERY_KEY } from './HouseholdsPage';

type ProfileRecord = {
  id: string;
  full_name: string;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  avatar?: string | null;
  member_since?: string | null;
  dob?: string | null;
  baptism_date?: string | null;
  bio?: string | null;
  roles?: string[] | null;
};

type HouseholdInfo = {
  name: string;
  address: string;
  phone: string;
  relationship: string;
  isPrimary: boolean;
  marriedDate?: string | null;
};

type HouseholdMember = {
  id: string;
  userId: string;
  name: string;
  relationship?: string | null;
  avatar?: string | null;
};

type HouseholdLookup = {
  id: string;
  name: string;
  address?: string | null;
  phone?: string | null;
};

type MinistryOption = {
  key: string;
  label: string;
};

type ProfilePageData = {
  profile: ProfileRecord;
  householdInfo: HouseholdInfo | null;
  householdId: string | null;
  householdMemberId: string | null;
  ministries: string[];
  ministryOptions: MinistryOption[];
  ministryKeys: string[];
  householdMembers: HouseholdMember[];
};

const APP_STORE_IMAGE_URL =
  'https://dqggtsiuqzwwjvugmovp.supabase.co/storage/v1/object/public/app-assets/app-assets/image/appStoreImg.png';
const PLAY_STORE_IMAGE_URL =
  'https://dqggtsiuqzwwjvugmovp.supabase.co/storage/v1/object/public/app-assets/app-assets/image/playStoreImg.png';

const APP_STORE_URL = 'https://apps.apple.com/ca/app/new-church-of-faith/id6752209128';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.alwinsley.newchurchoffaith';

const Page = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  background: var(--background);
  color: var(--onSurface);
`;

const HeaderLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 1rem;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const HeaderCard = styled.section`
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--outline);
  padding: 1.5rem 1.75rem;
  display: grid;
  gap: 1rem;
  margin-top: 20px;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: auto 1fr;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    text-align: center;
    justify-items: center;
  }
`;

const AvatarWrap = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--surfaceVariant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 700;
  color: var(--onSurface);
  border: 3px solid var(--outline);
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeaderText = styled.div`
  display: grid;
  gap: 0.3rem;
`;

const AppPromoText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--onSurface);
  font-weight: 600;
`;

const StoreBadges = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const StoreBadgeLink = styled.a`
  display: inline-flex;
`;

const StoreBadge = styled.img`
  width: 280px;
  max-width: 100%;
  height: auto;
`;

const Name = styled.h1`
  margin: 0;
  font-size: 1.9rem;
  color: var(--onSurface);
`;

const Title = styled.p`
  margin: 0;
  font-size: 1rem;
  color: var(--onSurfaceVariant);
`;

const Bio = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--onSurfaceVariant);
`;

const MetaLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  color: var(--onSurfaceVariant);
  font-size: 0.95rem;
`;

const MetaDivider = styled.span`
  color: var(--onSurfaceVariant);
`;

const Section = styled.section`
  display: grid;
  gap: 0.6rem;
  margin-top: 0px;
  padding-top: 20px;
  padding-bottom: 20px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  color: var(--onSurface);
`;

const Card = styled.div`
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--outline);
  padding: 1.5rem;
`;

const AppPromoCard = styled(Card)`
  display: grid;
  gap: 0.6rem;
  padding: 0.75rem;
  margin-top: 20px;
  max-width: 420px;
`;

const SubTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  color: var(--onSurface);
`;

const Label = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
`;

const Value = styled.span`
  color: var(--onSurface);
  font-weight: 600;
`;

const CardGrid = styled.div`
  display: grid;
  gap: 1.25rem;
`;

const HouseholdHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 0.75rem;
  padding: 0.45rem 0;
  border-bottom: 1px solid var(--outline);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const MemberRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const MemberPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  border-radius: 999px;
  font-size: 0.85rem;
  color: var(--onSurface);
`;

const MemberRemoveButton = styled.button`
  border: none;
  background: transparent;
  color: var(--error);
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const MemberAvatar = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--outline);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--onSurfaceVariant);
  overflow: hidden;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  background: rgba(215, 196, 173, 0.2);
  color: var(--primary);
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const EditButton = styled.button`
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  border-radius: 10px;
  padding: 0.55rem 1.2rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  border-radius: 10px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  cursor: pointer;
`;

const HelperText = styled.p`
  margin: 0.25rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
`;

const StatusBanner = styled.div<{ $tone: 'success' | 'error' }>`
  padding: 0.6rem 0.8rem;
  border-radius: 10px;
  background: ${({ $tone }) => ($tone === 'success' ? 'var(--successContainer)' : 'var(--errorContainer)')};
  color: ${({ $tone }) => ($tone === 'success' ? 'var(--onSuccessContainer)' : 'var(--onErrorContainer)')};
  font-weight: 600;
`;

const CompletionBanner = styled.div`
  background: var(--warningContainer);
  border: 1px solid var(--warning);
  border-radius: 10px;
  padding: 1rem 1.25rem;
  display: grid;
  gap: 0.5rem;
  color: var(--onWarningContainer);
  margin-top: 20px;
`;

const CompletionTitle = styled.div`
  font-weight: 700;
  font-size: 1rem;
`;

const CompletionList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  display: grid;
  gap: 0.35rem;
  font-size: 0.9rem;
`;

const AccordionToggle = styled.button<{ $open?: boolean }>`
  border: 1px solid var(--outline);
  background: ${({ $open }) => ($open ? 'var(--surfaceVariant)' : 'var(--surface)')};
  color: var(--onSurface);
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  cursor: pointer;
  margin-top: 20px;
`;

const AccordionContent = styled.div`
  margin-top: 0.75rem;
  display: grid;
  gap: 0.75rem;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;
  align-items: end;
`;

const DropdownList = styled.ul`
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0;
  border: 1px solid var(--outline);
  border-radius: 8px;
  max-height: 180px;
  overflow: auto;
  background: var(--surface);
`;

const DropdownItem = styled.li`
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  color: var(--onSurface);

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.9rem;
  color: var(--onSurface);
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 200;
`;

const ModalCard = styled(Card)`
  width: min(720px, 100%);
  max-height: 90vh;
  overflow: auto;
  overflow-x: hidden;
  border: 1px solid var(--outline);
  background: var(--surface);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--outline);
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  font-size: 1.1rem;
  cursor: pointer;
`;
const FormGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const Field = styled.label`
  display: grid;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const Input = styled.input`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  font-size: 0.95rem;
  color: var(--onSurface);
  background: var(--surface);

  &::placeholder {
    color: var(--onSurfaceVariant);
  }
`;

const Select = styled.select`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  font-size: 0.95rem;
  color: var(--onSurface);
  background: var(--surface);
`;

const TextArea = styled.textarea`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
  color: var(--onSurface);
  background: var(--surface);
  min-height: 90px;
  resize: vertical;

  &::placeholder {
    color: var(--onSurfaceVariant);
  }
`;

const FileInput = styled.input`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.45rem 0.55rem;
  font-size: 0.9rem;
  background: var(--surface);
  color: var(--onSurface);
`;

const AvatarPreview = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--outline);
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const SecondaryButton = styled(PrimaryButton)`
  background: var(--outline);
  color: var(--onSurface);
`;

const SmallButton = styled(SecondaryButton)`
  padding: 0.45rem 0.8rem;
  font-size: 0.9rem;
`;

const AccountGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`;

const AccountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid var(--outline);

  &:last-child {
    border-bottom: none;
  }
`;

const AccountText = styled.div`
  display: grid;
  gap: 0.15rem;
`;

const ActionTextButton = styled.button`
  border: none;
  background: none;
  color: var(--primary);
  font-weight: 600;
  cursor: pointer;
  padding: 0;
`;

const InlineLink = styled(Link)`
  color: var(--primary);
  font-weight: 600;
  text-decoration: none;
`;

const formatDateValue = (dateString?: string | null) => {
  if (!dateString) return '—';
  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    const utcDate = new Date(Date.UTC(year, month - 1, day));
    return utcDate.toLocaleDateString('en-US', { timeZone: 'UTC' });
  }
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString();
};

const formatMonthYear = (dateString?: string | null) => {
  if (!dateString) return '—';
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return formatDateValue(dateString);
  return parsed.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const RELATIONSHIP_OPTIONS = ['Husband', 'Spouse', 'Child', 'Extended', 'In-Law', 'Other'];

const ProfilePage: React.FC = () => {
  const { user, sendPasswordReset } = useAuth();
  const queryClient = useQueryClient();
  const [statusMessage, setStatusMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(
    null,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingHousehold, setIsSavingHousehold] = useState(false);
  const [isSavingMinistries, setIsSavingMinistries] = useState(false);
  const [isClearingHousehold, setIsClearingHousehold] = useState(false);
  const [isSavingChild, setIsSavingChild] = useState(false);
  const [removingHouseholdMemberId, setRemovingHouseholdMemberId] = useState<string | null>(null);
  const [showAddChild, setShowAddChild] = useState(false);
  const [childFirstName, setChildFirstName] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const [childDob, setChildDob] = useState('');
  const [showHouseholdEdit, setShowHouseholdEdit] = useState(true);
  const [showMinistryEdit, setShowMinistryEdit] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [households, setHouseholds] = useState<HouseholdLookup[]>([]);
  const [householdSearch, setHouseholdSearch] = useState('');
  const [selectedHouseholdId, setSelectedHouseholdId] = useState<string | null>(null);
  const [showCreateHousehold, setShowCreateHousehold] = useState(false);
  const [newHouseholdName, setNewHouseholdName] = useState('');
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    title: '',
    bio: '',
    email: '',
    phone: '',
    dob: '',
    baptismDate: '',
    avatar: '',
  });
  const [householdForm, setHouseholdForm] = useState({
    name: '',
    address: '',
    phone: '',
    relationship: '',
    isPrimary: false,
    marriedDate: '',
  });
  const [ministryForm, setMinistryForm] = useState<string[]>([]);

  const { data, isLoading, error, refetch } = useQuery<ProfilePageData | null>({
    queryKey: ['profile-page', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return null;

      const [profileRes, householdMemberRes, categoriesRes, ministriesRes] = await Promise.all([
        supabase
          .from('users')
          .select(
            'id, full_name, email, phone, title, avatar, member_since, dob, baptism_date, bio, roles',
          )
          .eq('id', user.id)
          .single(),
        supabase
          .from('household_members')
        .select('id, user_id, relationship, is_primary, married_date, household_id')
          .eq('user_id', user.id)
          .order('is_primary', { ascending: false })
          .order('joined_at', { ascending: true })
          .limit(1)
          .maybeSingle(),
        supabase.from('volunteer_categories').select('key, label').order('label', { ascending: true }),
        supabase
          .from('member_ministry_assignments')
          .select('category_key')
          .eq('member_id', user.id),
      ]);

      if (profileRes.error) {
        throw profileRes.error;
      }

      let householdInfo: HouseholdInfo | null = null;
      let householdId: string | null = null;
      let householdMemberId: string | null = null;
      let householdMembers: HouseholdMember[] = [];
      const memberRow = householdMemberRes.data as
        | {
            id?: string | null;
            household_id?: string | null;
            relationship?: string | null;
            is_primary?: boolean | null;
            married_date?: string | null;
          }
        | null
        | undefined;
      if (memberRow?.household_id) {
        householdId = memberRow.household_id ?? null;
        householdMemberId = memberRow.id ?? null;
        const { data: householdData } = await supabase
          .from('households')
          .select('id, name, address, phone')
          .eq('id', memberRow.household_id)
          .single();

        householdInfo = {
          name: householdData?.name ?? '',
          address: householdData?.address ?? '',
          phone: householdData?.phone ?? '',
          relationship: memberRow.relationship ?? '',
          isPrimary: Boolean(memberRow.is_primary),
          marriedDate: memberRow.married_date ?? null,
        };

        const { data: memberData } = await supabase
          .from('household_members')
          .select('id, relationship, user:users(id, full_name, avatar)')
          .eq('household_id', memberRow.household_id)
          .order('is_primary', { ascending: false })
          .order('id', { ascending: true });

        householdMembers = ((memberData as unknown[]) ?? []).map((row) => {
          const cast = row as {
            id: string;
            relationship?: string | null;
            user?: { id: string; full_name?: string | null; avatar?: string | null } | Array<{
              id: string;
              full_name?: string | null;
              avatar?: string | null;
            }> | null;
          };
          const userRecord = Array.isArray(cast.user) ? cast.user[0] : cast.user;
          return {
            id: cast.id,
            userId: userRecord?.id ?? '',
            name: userRecord?.full_name ?? 'Member',
            relationship: cast.relationship ?? null,
            avatar: userRecord?.avatar ?? null,
          };
        });
      }

      const categoriesList = (categoriesRes.data as { key: string; label?: string | null }[]) ?? [];
      const ministryOptions = categoriesList.map((category) => ({
        key: String(category.key),
        label: String(category.label ?? category.key),
      }));
      const categoryLabelMap = new Map<string, string>(
        ministryOptions.map((category) => [category.key, category.label]),
      );
      const assignments = (ministriesRes.data as { category_key: string }[]) ?? [];
      const ministryKeys = assignments.map((assignment) => String(assignment.category_key)).filter(Boolean);
      const ministryLabels = assignments
        .map((assignment) => categoryLabelMap.get(String(assignment.category_key)) ?? String(assignment.category_key))
        .sort((a, b) => a.localeCompare(b));

      return {
        profile: profileRes.data as ProfileRecord,
        householdInfo,
        householdId,
        householdMemberId,
        ministries: ministryLabels,
        ministryOptions,
        ministryKeys,
        householdMembers,
      };
    },
    staleTime: 5 * 60_000,
  });

  const profile = data?.profile;
  const initials = useMemo(() => {
    const name = profile?.full_name || user?.user_metadata?.full_name || user?.email || 'User';
    return name
      .split(' ')
      .map((part: string) => part.trim()[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }, [profile?.full_name, user?.user_metadata?.full_name, user?.email]);
  const getInitialsFromName = (name: string) =>
    name
      .split(' ')
      .map((part: string) => part.trim()[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();

  const handlePasswordReset = async () => {
    const email = profile?.email || user?.email;
    if (!email) {
      setStatusMessage({ tone: 'error', text: 'Add an email address to request a password reset.' });
      return;
    }
    try {
      await sendPasswordReset(email);
      setStatusMessage({ tone: 'success', text: 'Password reset email sent. Check your inbox.' });
    } catch (err) {
      const message = (err as { message?: string }).message ?? 'Unable to send reset email right now.';
      setStatusMessage({ tone: 'error', text: message });
    }
  };

  useEffect(() => {
    if (!profile) return;
    setProfileForm({
      fullName: profile.full_name ?? '',
      title: profile.title ?? '',
      bio: profile.bio ?? '',
      email: profile.email ?? '',
      phone: profile.phone ?? '',
      dob: profile.dob ?? '',
      baptismDate: profile.baptism_date ?? '',
      avatar: profile.avatar ?? '',
    });
  }, [profile]);

  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  useEffect(() => {
    if (!data) return;
    setHouseholdForm({
      name: data.householdInfo?.name ?? '',
      address: data.householdInfo?.address ?? '',
      phone: data.householdInfo?.phone ?? '',
      relationship: data.householdInfo?.relationship ?? '',
      isPrimary: Boolean(data.householdInfo?.isPrimary),
      marriedDate: data.householdInfo?.marriedDate ?? '',
    });
    setHouseholdSearch(data.householdInfo?.name ?? '');
    setSelectedHouseholdId(null);
    setMinistryForm(data.ministryKeys ?? []);
  }, [data]);

  useEffect(() => {
    const loadHouseholds = async () => {
      const PAGE = 1000;
      const all: HouseholdLookup[] = [];
      let from = 0;
      while (true) {
        const to = from + PAGE - 1;
        const { data: chunk } = await supabase
          .from('households')
          .select('id, name, address, phone')
          .order('name', { ascending: true })
          .range(from, to);
        const rows = (chunk as HouseholdLookup[]) ?? [];
        all.push(...rows);
        if (rows.length < PAGE) break;
        from += PAGE;
      }
      setHouseholds(all);
    };
    loadHouseholds();
  }, []);

  const filteredHouseholds = useMemo(() => {
    const search = householdSearch.trim().toLowerCase();
    if (!search) return [];
    return households.filter((item) => item.name.toLowerCase().includes(search));
  }, [householdSearch, households]);

  const handleHouseholdSearchChange = (value: string) => {
    setHouseholdSearch(value);
    setSelectedHouseholdId(null);
    setHouseholdForm((prev) => ({ ...prev, name: value }));
  };

  const handleSelectHousehold = (selected: HouseholdLookup) => {
    setSelectedHouseholdId(selected.id);
    setHouseholdSearch(selected.name);
    setShowCreateHousehold(false);
    setNewHouseholdName('');
    setHouseholdForm((prev) => ({
      ...prev,
      name: selected.name,
      address: selected.address ?? '',
      phone: selected.phone ?? '',
    }));
  };

  const handleCreateHousehold = async () => {
    if (!user?.id) return;
    const trimmedName = newHouseholdName.trim();
    if (!trimmedName) return;
    setIsSavingHousehold(true);
    setStatusMessage(null);
    try {
      const { data: createdHousehold, error: createError } = await supabase
        .from('households')
        .insert({
          name: trimmedName,
          address: householdForm.address.trim() || null,
          phone: householdForm.phone.trim() || null,
          primary_contact_id: user.id,
        })
        .select('id, name, address, phone')
        .single();
      if (createError) throw createError;
      const normalized = createdHousehold as HouseholdLookup;
      setHouseholds((prev) => [normalized, ...prev]);
      handleSelectHousehold(normalized);
      setShowCreateHousehold(false);
      setNewHouseholdName('');
      setStatusMessage({ tone: 'success', text: 'Household created.' });
      await queryClient.invalidateQueries({ queryKey: ADMIN_HOUSEHOLDS_QUERY_KEY });
    } catch (err) {
      const message = (err as { message?: string }).message ?? 'Unable to create household right now.';
      setStatusMessage({ tone: 'error', text: message });
    } finally {
      setIsSavingHousehold(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;
    setIsSaving(true);
    setStatusMessage(null);
    try {
      let avatarUrl = profileForm.avatar.trim() || null;
      const trimmedEmail = profileForm.email.trim();
      if (trimmedEmail && !/\S+@\S+\.\S+/.test(trimmedEmail)) {
        throw new Error('Please enter a valid email address.');
      }
      if (avatarFile) {
        if (avatarFile.size > 5 * 1024 * 1024) {
          throw new Error('Please upload an image smaller than 5MB.');
        }
        const ext = avatarFile.name.split('.').pop() || 'jpg';
        const fileName = `avatars/${user.id}_${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('post-images')
          .upload(fileName, avatarFile, { upsert: true });
        if (uploadError) {
          throw new Error(uploadError.message);
        }
        const { data: publicData } = supabase.storage.from('post-images').getPublicUrl(fileName);
        avatarUrl = publicData.publicUrl;
      }

      const payload = {
        full_name: profileForm.fullName.trim(),
        title: profileForm.title.trim() || null,
        bio: profileForm.bio.trim() || null,
        email: trimmedEmail || null,
        phone: profileForm.phone.trim() || null,
        dob: profileForm.dob || null,
        baptism_date: profileForm.baptismDate || null,
        avatar: avatarUrl,
      };
      const { error: updateError } = await supabase.from('users').update(payload).eq('id', user.id);
      if (updateError) throw updateError;
      if (trimmedEmail && trimmedEmail !== user.email) {
        const { error: authEmailError } = await supabase.auth.updateUser({ email: trimmedEmail });
        if (authEmailError) throw authEmailError;
      }
      await supabase.auth.updateUser({
        data: {
          full_name: payload.full_name,
          phone: payload.phone ?? undefined,
          avatar_url: avatarUrl ?? undefined,
        },
      });
      // Auth metadata updates can fire sync_auth_user_profile; re-assert contact phone
      // so a stale auth.users.phone value cannot overwrite the profile phone we just saved.
      const { error: phoneReassertError } = await supabase
        .from('users')
        .update({ phone: payload.phone })
        .eq('id', user.id);
      if (phoneReassertError) throw phoneReassertError;
      setStatusMessage({ tone: 'success', text: 'Profile updated.' });
      setIsEditing(false);
      setAvatarFile(null);
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
        setAvatarPreview(null);
      }
      await refetch();
    } catch (err) {
      const message = (err as { message?: string }).message ?? 'Unable to update profile right now.';
      setStatusMessage({ tone: 'error', text: message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveHousehold = async () => {
    if (!user?.id) return;
    const targetHouseholdId = selectedHouseholdId ?? data?.householdId ?? null;
    if (!targetHouseholdId) {
      setStatusMessage({ tone: 'error', text: 'Select a household before saving.' });
      return;
    }
    setIsSavingHousehold(true);
    setStatusMessage(null);
    try {
      const { error: householdError } = await supabase
        .from('households')
        .update({
          name: householdForm.name.trim(),
          address: householdForm.address.trim() || null,
          phone: householdForm.phone.trim() || null,
        })
        .eq('id', targetHouseholdId);
      if (householdError) throw householdError;

      if (data?.householdMemberId) {
        const { error: memberError } = await supabase
          .from('household_members')
          .update({
            household_id: targetHouseholdId,
            relationship: householdForm.relationship.trim() || null,
            is_primary: householdForm.isPrimary,
            married_date: householdForm.marriedDate || null,
          })
          .eq('id', data.householdMemberId);
        if (memberError) throw memberError;
      } else if (user?.id) {
        const { error: memberInsertError } = await supabase.from('household_members').insert({
          household_id: targetHouseholdId,
          user_id: user.id,
          relationship: householdForm.relationship.trim() || null,
          is_primary: householdForm.isPrimary,
          married_date: householdForm.marriedDate || null,
        });
        if (memberInsertError) throw memberInsertError;
      }

      setStatusMessage({ tone: 'success', text: 'Household updated.' });
      await refetch();
    } catch (err) {
      const message = (err as { message?: string }).message ?? 'Unable to update household right now.';
      setStatusMessage({ tone: 'error', text: message });
    } finally {
      setIsSavingHousehold(false);
    }
  };

  const handleCreateChild = async () => {
    const householdId = selectedHouseholdId ?? data?.householdId ?? null;
    if (!householdId) {
      setStatusMessage({ tone: 'error', text: 'Select or create a household before adding a child.' });
      return;
    }
    if (!childFirstName.trim() || !childLastName.trim()) {
      setStatusMessage({ tone: 'error', text: 'First and last name are required.' });
      return;
    }
    setIsSavingChild(true);
    setStatusMessage(null);
    try {
      const { error } = await supabase.functions.invoke('create-child-account', {
        body: {
          firstName: childFirstName.trim(),
          lastName: childLastName.trim(),
          dateOfBirth: childDob || null,
          householdId,
        },
      });
      if (error) throw error;
      setChildFirstName('');
      setChildLastName('');
      setChildDob('');
      setShowAddChild(false);
      setStatusMessage({ tone: 'success', text: 'Child was saved.' });
      await refetch();
    } catch (err) {
      const message = (err as { message?: string }).message ?? 'Unable to add child right now.';
      setStatusMessage({ tone: 'error', text: message });
    } finally {
      setIsSavingChild(false);
    }
  };

  const handleClearHousehold = async () => {
    if (!user?.id) return;
    setIsClearingHousehold(true);
    setStatusMessage(null);
    try {
      const { error: deleteError } = await supabase
        .from('household_members')
        .delete()
        .eq('user_id', user.id);
      if (deleteError) throw deleteError;
      setHouseholdForm({
        name: '',
        address: '',
        phone: '',
        relationship: '',
        isPrimary: false,
        marriedDate: '',
      });
      setHouseholdSearch('');
      setSelectedHouseholdId(null);
      setStatusMessage({ tone: 'success', text: 'Household cleared.' });
      await refetch();
    } catch (err) {
      const message = (err as { message?: string }).message ?? 'Unable to clear household right now.';
      setStatusMessage({ tone: 'error', text: message });
    } finally {
      setIsClearingHousehold(false);
    }
  };

  const canManageOtherHouseholdMembers = Boolean(data?.householdInfo?.isPrimary);

  const handleRemoveHouseholdMember = async (member: HouseholdMember) => {
    if (!member.id) return;
    const confirmed = window.confirm(`Remove ${member.name} from your household?`);
    if (!confirmed) return;

    setRemovingHouseholdMemberId(member.id);
    setStatusMessage(null);
    try {
      const { error: deleteError } = await supabase
        .from('household_members')
        .delete()
        .eq('id', member.id);
      if (deleteError) throw deleteError;
      setStatusMessage({ tone: 'success', text: `${member.name} was removed from your household.` });
      await refetch();
    } catch (err) {
      const message = (err as { message?: string }).message ?? 'Unable to remove this household member right now.';
      setStatusMessage({ tone: 'error', text: message });
    } finally {
      setRemovingHouseholdMemberId(null);
    }
  };

  const handleSaveMinistries = async () => {
    if (!user?.id) return;
    setIsSavingMinistries(true);
    setStatusMessage(null);
    try {
      const { error: deleteError } = await supabase
        .from('member_ministry_assignments')
        .delete()
        .eq('member_id', user.id);
      if (deleteError) throw deleteError;

      const rows = ministryForm.map((key) => ({ member_id: user.id, category_key: key }));
      if (rows.length > 0) {
        const { error: insertError } = await supabase
          .from('member_ministry_assignments')
          .insert(rows);
        if (insertError) throw insertError;
      }
      setStatusMessage({ tone: 'success', text: 'Ministries updated.' });
      await refetch();
    } catch (err) {
      const message = (err as { message?: string }).message ?? 'Unable to update ministries right now.';
      setStatusMessage({ tone: 'error', text: message });
    } finally {
      setIsSavingMinistries(false);
    }
  };

  if (isLoading) {
    return (
      <Page>
        <HeaderCard>
          <Name>Loading profile…</Name>
        </HeaderCard>
      </Page>
    );
  }

  if (error || !profile) {
    return (
      <Page>
        <HeaderCard>
          <Name>Unable to load profile</Name>
          <HelperText>Try refreshing the page to load your details.</HelperText>
          <PrimaryButton type="button" onClick={() => refetch()}>
            Retry
          </PrimaryButton>
        </HeaderCard>
      </Page>
    );
  }

  const memberSinceValue = formatMonthYear(profile.member_since || user?.created_at);
  const primaryMinistry = data.ministries[0];
  const isSuperAdmin = hasSuperAdminRole(profile.roles);
  const missingItems = [
    !profile.full_name ? 'Full name' : null,
    !profile.email ? 'Email address' : null,
    !profile.phone ? 'Phone number' : null,
    !profile.dob ? 'Date of birth' : null,
    !profile.baptism_date ? 'Date of baptism' : null,
    !data.householdInfo?.name ? 'Household name' : null,
    !data.householdInfo?.address ? 'Household address' : null,
  ].filter((item): item is string => Boolean(item));

  return (
    <Page>
      {missingItems.length > 0 ? (
        <CompletionBanner>
          <CompletionTitle>Please complete your profile</CompletionTitle>
          <div>Finish these items to complete your profile:</div>
          <CompletionList>
            {missingItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </CompletionList>
        </CompletionBanner>
      ) : null}
     
      {isEditing ? (
        <ModalOverlay onClick={() => setIsEditing(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <SectionTitle>Update Profile</SectionTitle>
              <CloseButton type="button" onClick={() => setIsEditing(false)}>
                ✕
              </CloseButton>
            </ModalHeader>
            <FormGrid>
              <FullRow>
                <Field>
                  Avatar
                  {avatarPreview || profileForm.avatar ? (
                    <AvatarPreview src={avatarPreview || profileForm.avatar} alt="Avatar preview" />
                  ) : null}
                  <FileInput
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      if (avatarPreview) {
                        URL.revokeObjectURL(avatarPreview);
                      }
                      setAvatarFile(file);
                      setAvatarPreview(file ? URL.createObjectURL(file) : null);
                    }}
                  />
                </Field>
              </FullRow>
              <Field>
                Full Name
                <Input
                  value={profileForm.fullName}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, fullName: event.target.value }))}
                />
              </Field>

              <Field>
                Email
                <Input
                  type="email"
                  value={profileForm.email}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, email: event.target.value }))}
                  placeholder="you@example.com"
                />
              </Field>
             
              <Field>
                Phone
                <Input
                  value={profileForm.phone}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, phone: event.target.value }))}
                  placeholder="ie. 3217899874"
                />
              </Field>
              <Field>
                Date of Birth
                <Input
                  type="date"
                  value={profileForm.dob}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, dob: event.target.value }))}
                />
              </Field>
              <Field>
                Date of Baptism
                <Input
                  type="date"
                  value={profileForm.baptismDate}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, baptismDate: event.target.value }))}
                />
              </Field>
            </FormGrid>
            <Field style={{ marginTop: '1rem' }}>
              Bio
              <TextArea
                value={profileForm.bio}
                onChange={(event) => setProfileForm((prev) => ({ ...prev, bio: event.target.value }))}
              />
            </Field>
            <ButtonRow>
              <SecondaryButton type="button" onClick={() => setIsEditing(false)} disabled={isSaving}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="button" onClick={handleSaveProfile} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </PrimaryButton>
            </ButtonRow>

            <AccordionToggle type="button" $open={showHouseholdEdit} onClick={() => setShowHouseholdEdit((prev) => !prev)}>
              Edit Household
              <span>{showHouseholdEdit ? '−' : '+'}</span>
            </AccordionToggle>
            {showHouseholdEdit ? (
              <AccordionContent>
                <FormGrid>
                  <FullRow>
                    <Field>
                      Household Name
                      <FieldRow>
                        <div>
                          <Input
                            value={householdSearch}
                            onChange={(event) => handleHouseholdSearchChange(event.target.value)}
                            placeholder="Search households..."
                          />
                          {householdSearch.trim().length > 0 && !showCreateHousehold ? (
                            filteredHouseholds.length > 0 ? (
                              <DropdownList>
                                {filteredHouseholds.map((item) => (
                                  <DropdownItem key={item.id} onClick={() => handleSelectHousehold(item)}>
                                    {item.name}
                                  </DropdownItem>
                                ))}
                              </DropdownList>
                            ) : (
                              <HelperText>No matching households found.</HelperText>
                            )
                          ) : null}
                        </div>
                        <SmallButton type="button" onClick={() => setShowCreateHousehold(true)}>
                          Create New
                        </SmallButton>
                      </FieldRow>
                    </Field>
                  </FullRow>
                  {showCreateHousehold ? (
                    <FullRow>
                      <Field>
                        New Household Name
                        <Input
                          value={newHouseholdName}
                          onChange={(event) => setNewHouseholdName(event.target.value)}
                          placeholder="Enter household name"
                        />
                      </Field>
                    </FullRow>
                  ) : null}
                  <FullRow>
                    <Field>
                      Household Address
                      <Input
                        value={householdForm.address}
                        onChange={(event) => setHouseholdForm((prev) => ({ ...prev, address: event.target.value }))}
                      />
                    </Field>
                  </FullRow>
                  <FullRow>
                    <Field>
                      Relationship
                      <Select
                        value={householdForm.relationship}
                        onChange={(event) => setHouseholdForm((prev) => ({ ...prev, relationship: event.target.value }))}
                      >
                        <option value="">Select relationship</option>
                        {RELATIONSHIP_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </FullRow>
                  {['Husband', 'Spouse'].includes(householdForm.relationship) ? (
                    <FullRow>
                      <Field>
                        Married Date
                        <Input
                          type="date"
                          value={householdForm.marriedDate}
                          onChange={(event) => setHouseholdForm((prev) => ({ ...prev, marriedDate: event.target.value }))}
                        />
                      </Field>
                    </FullRow>
                  ) : null}
                  <CheckboxRow>
                    <Checkbox
                      type="checkbox"
                      checked={householdForm.isPrimary}
                      onChange={(event) => setHouseholdForm((prev) => ({ ...prev, isPrimary: event.target.checked }))}
                    />
                    Primary contact for household
                  </CheckboxRow>
                  <FullRow>
                    <SmallButton type="button" onClick={() => setShowAddChild((prev) => !prev)}>
                      {showAddChild ? 'Cancel Add Child' : 'Add Child'}
                    </SmallButton>
                  </FullRow>
                  {showAddChild ? (
                    <>
                      <FullRow>
                        <Field>
                          Child First Name
                          <Input
                            value={childFirstName}
                            onChange={(event) => setChildFirstName(event.target.value)}
                          />
                        </Field>
                      </FullRow>
                      <FullRow>
                        <Field>
                          Child Last Name
                          <Input
                            value={childLastName}
                            onChange={(event) => setChildLastName(event.target.value)}
                          />
                        </Field>
                      </FullRow>
                      <FullRow>
                        <Field>
                          Child Date of Birth
                          <Input
                            type="date"
                            value={childDob}
                            onChange={(event) => setChildDob(event.target.value)}
                          />
                        </Field>
                      </FullRow>
                      <ButtonRow style={{ justifyContent: 'flex-start' }}>
                        <PrimaryButton type="button" onClick={handleCreateChild} disabled={isSavingChild}>
                          {isSavingChild ? 'Saving...' : 'Save Child'}
                        </PrimaryButton>
                      </ButtonRow>
                    </>
                  ) : null}
                </FormGrid>
                <ButtonRow>
                  <PrimaryButton
                    type="button"
                    onClick={showCreateHousehold ? handleCreateHousehold : handleSaveHousehold}
                    disabled={
                      isSavingHousehold ||
                      (showCreateHousehold && !newHouseholdName.trim())
                    }
                  >
                    {isSavingHousehold
                      ? showCreateHousehold
                        ? 'Creating...'
                        : 'Saving...'
                      : showCreateHousehold
                        ? 'Create Household'
                        : 'Save Household'}
                  </PrimaryButton>
                  {showCreateHousehold ? (
                    <SecondaryButton
                      type="button"
                      onClick={() => {
                        setShowCreateHousehold(false);
                        setNewHouseholdName('');
                      }}
                      disabled={isSavingHousehold}
                    >
                      Cancel
                    </SecondaryButton>
                  ) : (
                    <SecondaryButton
                      type="button"
                      onClick={handleClearHousehold}
                      disabled={isClearingHousehold || isSavingHousehold}
                    >
                      {isClearingHousehold ? 'Clearing...' : 'Clear Household'}
                    </SecondaryButton>
                  )}
                </ButtonRow>
              </AccordionContent>
            ) : null}

            <AccordionToggle type="button" $open={showMinistryEdit} onClick={() => setShowMinistryEdit((prev) => !prev)}>
              Edit Ministries
              <span>{showMinistryEdit ? '−' : '+'}</span>
            </AccordionToggle>
            {showMinistryEdit ? (
              <AccordionContent>
                {data.ministryOptions.length === 0 ? (
                  <HelperText>No ministry categories available.</HelperText>
                ) : (
                  data.ministryOptions.map((option) => (
                    <CheckboxRow key={option.key}>
                      <Checkbox
                        type="checkbox"
                        checked={ministryForm.includes(option.key)}
                        onChange={(event) =>
                          setMinistryForm((prev) =>
                            event.target.checked
                              ? [...prev, option.key]
                              : prev.filter((item) => item !== option.key),
                          )
                        }
                      />
                      {option.label}
                    </CheckboxRow>
                  ))
                )}
                <ButtonRow>
                  <PrimaryButton type="button" onClick={handleSaveMinistries} disabled={isSavingMinistries}>
                    {isSavingMinistries ? 'Saving...' : 'Save Ministries'}
                  </PrimaryButton>
                </ButtonRow>
              </AccordionContent>
            ) : null}
          </ModalCard>
        </ModalOverlay>
      ) : null}

      <HeaderLayout>
        <HeaderCard>
          <HeaderRow>
            <AvatarWrap>
              {profile.avatar ? <AvatarImg src={profile.avatar} alt={profile.full_name} /> : initials}
            </AvatarWrap>
            <HeaderText>
              <Name>{profile.full_name || 'Church Partner'}</Name>
              {profile.title ? <Title>{profile.title}</Title> : null}
              {profile.bio ? <Bio>{profile.bio}</Bio> : null}
              <MetaLine>
                <span>Member since {memberSinceValue}</span>
                {primaryMinistry ? (
                  <>
                    <MetaDivider>•</MetaDivider>
                    <span>{primaryMinistry}</span>
                  </>
                ) : null}
              </MetaLine>
            </HeaderText>
            <HeaderActions>
              <EditButton type="button" onClick={() => setIsEditing(true)}>
                Edit Profile
              </EditButton>
            </HeaderActions>
          </HeaderRow>
        </HeaderCard>
        <AppPromoCard>
         
          <AppPromoText>Download our mobile app to stay up-to-date.</AppPromoText>
          <StoreBadges>
            <StoreBadgeLink href={APP_STORE_URL} target="_blank" rel="noreferrer">
              <StoreBadge src={APP_STORE_IMAGE_URL} alt="Download on the App Store" loading="lazy" />
            </StoreBadgeLink>
            <StoreBadgeLink href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
              <StoreBadge src={PLAY_STORE_IMAGE_URL} alt="Get it on Google Play" loading="lazy" />
            </StoreBadgeLink>
          </StoreBadges>
        </AppPromoCard>
      </HeaderLayout>

      {statusMessage ? <StatusBanner $tone={statusMessage.tone}>{statusMessage.text}</StatusBanner> : null}

      <Section>
        <SectionTitle>Household Information</SectionTitle>
        <Card>
          {data.householdInfo ? (
            <>
              <CardGrid>
                <HouseholdHeader>
                  <div>
                    <Value>{data.householdInfo.name || 'Household'}</Value>
                  </div>
                </HouseholdHeader>
                <InfoRow>
                  <Label>Address</Label>
                  <Value>{data.householdInfo.address || '—'}</Value>
                </InfoRow>
               
                <InfoRow>
                  <Label>Relationship</Label>
                  <Value>{data.householdInfo.relationship || '—'}</Value>
                </InfoRow>
                {data.householdInfo.marriedDate ||
                data.householdInfo.relationship === 'Husband' ||
                data.householdInfo.relationship === 'Spouse' ? (
                  <InfoRow>
                    <Label>Married Date</Label>
                    <Value>{formatDateValue(data.householdInfo.marriedDate)}</Value>
                  </InfoRow>
                ) : null}
                <InfoRow>
                  <Label>Primary Contact</Label>
                  <Value>{data.householdInfo.isPrimary ? 'Yes' : 'No'}</Value>
                </InfoRow>
              </CardGrid>
              <SubTitle style={{ marginTop: '1rem' }}>Family Members</SubTitle>
              {data.householdMembers.length === 0 ? (
                <HelperText>No household members listed.</HelperText>
              ) : (
                <MemberRow>
                  {data.householdMembers.map((member) => (
                    <MemberPill key={member.id}>
                      <MemberAvatar>
                        {member.avatar ? (
                          <AvatarImg src={member.avatar} alt={member.name} />
                        ) : (
                          getInitialsFromName(member.name)
                        )}
                      </MemberAvatar>
                      {member.name}
                      {member.relationship ? ` (${member.relationship})` : ''}
                      {canManageOtherHouseholdMembers && member.userId !== user?.id ? (
                        <MemberRemoveButton
                          type="button"
                          onClick={() => void handleRemoveHouseholdMember(member)}
                          disabled={removingHouseholdMemberId === member.id}
                        >
                          {removingHouseholdMemberId === member.id ? 'Removing…' : 'Remove'}
                        </MemberRemoveButton>
                      ) : null}
                    </MemberPill>
                  ))}
                </MemberRow>
              )}
              {data.householdMembers.length > 1 && !canManageOtherHouseholdMembers ? (
                <HelperText>Only the primary contact can remove household members.</HelperText>
              ) : null}
            </>
          ) : (
            <HelperText>No household information available.</HelperText>
          )}
        </Card>
      </Section>

      <Section>
        <SectionTitle>Ministry Involvement</SectionTitle>
        <Card>
          {data.ministries.length === 0 ? (
            <HelperText>No ministries assigned.</HelperText>
          ) : (
            <TagList>
              {data.ministries.map((ministry) => (
                <Tag key={ministry}>{ministry}</Tag>
              ))}
            </TagList>
          )}
         
        </Card>
      </Section>

      <Section>
        <SectionTitle>Account Details</SectionTitle>
        <AccountGrid>
          <Card>
            <AccountRow>
              <AccountText>
                <Label>Login Email</Label>
                <Value>{profile.email || '—'}</Value>
              </AccountText>
             
            </AccountRow>
            <AccountRow>
              <AccountText>
                <Label>Phone Number</Label>
                <Value>{profile.phone || '—'}</Value>
              </AccountText>
             
            </AccountRow>
            <AccountRow>
              <AccountText>
                <Label>Date of Birth</Label>
                <Value>{formatDateValue(profile.dob)}</Value>
              </AccountText>
            </AccountRow>
            <AccountRow>
              <AccountText>
                <Label>Date of Baptism</Label>
                <Value>{formatDateValue(profile.baptism_date)}</Value>
              </AccountText>
            </AccountRow>
          </Card>
          <Card>
            
            <AccountRow>
              <AccountText>
                <Label>Security</Label>
                <Value>Reset your password</Value>
              </AccountText>
              <ActionTextButton type="button" onClick={handlePasswordReset}>
                Send Reset Email
              </ActionTextButton>
            </AccountRow>
          </Card>
        </AccountGrid>
      </Section>

      

      {isSuperAdmin ? (
        <Section>
          <SectionTitle>👑 Super Admin</SectionTitle>
          <Card>
            <AccountRow>
              <AccountText>
                <Value>Super Admin Panel</Value>
                <HelperText>Manage app configurations and admin settings.</HelperText>
              </AccountText>
              <InlineLink to="/super-admin">Open</InlineLink>
            </AccountRow>
            <AccountRow>
              <AccountText>
                <Value>Role Management</Value>
                <HelperText>Manage user roles and permissions.</HelperText>
              </AccountText>
              <InlineLink to="/admin/users">Open</InlineLink>
            </AccountRow>
          </Card>
        </Section>
      ) : null}
    </Page>
  );
};

export default ProfilePage;
